import { useMemo } from 'react';

function fmt(n, digits = 1) {
  if (n == null || Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 100) return Math.round(n).toString();
  return Number(n).toFixed(digits);
}

/**
 * Chart with current value, min/avg/max, and labeled sparkline.
 */
export default function Sparkline({
  data = [],
  color = '#0f766e',
  height = 72,
  max: fixedMax,
  unit = '',
  digits = 1,
}) {
  const stats = useMemo(() => {
    if (!data.length) {
      return { current: null, min: null, max: null, avg: null, ceiling: fixedMax ?? 100 };
    }
    const min = Math.min(...data);
    const max = Math.max(...data);
    const avg = data.reduce((a, b) => a + b, 0) / data.length;
    const current = data[data.length - 1];
    const ceiling = fixedMax != null
      ? fixedMax
      : Math.max(max * 1.15, max === 0 ? 1 : max);
    return { current, min, max, avg, ceiling };
  }, [data, fixedMax]);

  const points = useMemo(() => {
    if (!data.length) return '';
    const w = 200;
    const h = height - 8;
    const ceiling = stats.ceiling || 1;
    return data
      .map((v, i) => {
        const x = data.length === 1 ? w / 2 : (i / (data.length - 1)) * w;
        const y = h - (Math.min(Math.max(v, 0), ceiling) / ceiling) * h + 4;
        return `${x},${y}`;
      })
      .join(' ');
  }, [data, height, stats.ceiling]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#0f0f1a', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
          {fmt(stats.current, digits)}
          {unit && <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', marginLeft: 4 }}>{unit}</span>}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right', lineHeight: 1.35, fontVariantNumeric: 'tabular-nums' }}>
          <div>min {fmt(stats.min, digits)}</div>
          <div>avg {fmt(stats.avg, digits)}</div>
          <div>max {fmt(stats.max, digits)}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          fontSize: 9, color: '#94a3b8', fontVariantNumeric: 'tabular-nums', padding: '2px 0', minWidth: 28, textAlign: 'right',
        }}>
          <span>{fmt(stats.ceiling, digits)}</span>
          <span>{fmt((stats.ceiling || 0) / 2, digits)}</span>
          <span>0</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {!data.length ? (
            <svg width="100%" height={height} viewBox="0 0 200 72" preserveAspectRatio="none">
              <line x1="0" y1={height / 2} x2="200" y2={height / 2} stroke="#e2e8f0" strokeWidth="1" />
            </svg>
          ) : (
            <svg width="100%" height={height} viewBox={`0 0 200 ${height}`} preserveAspectRatio="none">
              <line x1="0" y1={4} x2="200" y2={4} stroke="#f1f5f9" strokeWidth="1" />
              <line x1="0" y1={height / 2} x2="200" y2={height / 2} stroke="#f1f5f9" strokeWidth="1" />
              <line x1="0" y1={height - 4} x2="200" y2={height - 4} stroke="#f1f5f9" strokeWidth="1" />
              <polyline
                fill="none"
                stroke={color}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                points={points}
              />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}
